<script>
export default {
  name: 'WorkOrderInstallTab',
  props: {
    dataErp: {
      type: Array,
      default: () => [],
    },

    gpsConfiguration: {
      type: [String, Number],
      required: true,
    },
    gpsConfigurationOptions: {
      type: Array,
      default: () => [],
    },
    loadingCustomProfiles: {
      type: Boolean,
      default: false,
    },

    techniciansNeeded: {
      type: [String, Number],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    installDuration: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      technicianQty: this.techniciansNeeded,
      selectedGpsConfigurations: {},
    }
  },
  computed: {
    canUpdateTechnicianQty() {
      const technicianQty = Number(this.technicianQty)

      return !this.loading && this.technicianQty !== '' && Number.isInteger(technicianQty) && technicianQty >= 0 && technicianQty !== Number(this.techniciansNeeded)
    },
    vehicleCount() {
      return this.dataErp.length
    },
    normalizedGpsConfigurationOptions() {
      const defaultOption = {
        value: '__default__',
        label: 'Default',
        name: 'Default',
        code: '',
        profile: null,
        isDefault: true,
      }
      const profileOptions = this.gpsConfigurationOptions
        .filter((profile) => profile && (profile.custom_profile_id || profile.id))
        .map((profile) => ({
          value: String(profile.custom_profile_id || profile.id),
          label: this.customProfileLabel(profile),
          name: profile.name,
          code: profile.code,
          profile,
          isDefault: false,
        }))

      return [defaultOption, ...profileOptions]
    },
    installPackages() {
      const packageMap = this.dataErp.reduce((acc, item) => {
        const products = Array.isArray(item.product) ? item.product : []
        const reservedProducts = products
          .map((product) => product.reserved_product_id)
          .filter((product) => product && product.id && product.name)
          .map((product) => ({
            id: product.id,
            name: product.name,
          }))
          .sort((firstProduct, secondProduct) => firstProduct.id - secondProduct.id)

        if (!reservedProducts.length) return acc

        const key = reservedProducts.map((product) => product.id).join('-')

        if (!acc[key]) {
          acc[key] = {
            key,
            title: reservedProducts.map((product) => product.name).join(' + '),
            quantity: 0,
            packageNames: [],
            reservedProductIds: reservedProducts.map((product) => product.id),
            dataErpIds: [],
            customProfileId: null,
            gpsConfiguration: null,
          }
        }

        acc[key].quantity += 1
        acc[key].packageNames.push(item.name || `ID ${item.id}`)
        acc[key].dataErpIds.push(item.id)
        acc[key].customProfileId = acc[key].customProfileId || this.getItemCustomProfileId(item)
        acc[key].gpsConfiguration = acc[key].gpsConfiguration || this.getItemGpsConfiguration(item)

        return acc
      }, {})

      return Object.values(packageMap)
    },
  },
  watch: {
    techniciansNeeded(value) {
      this.technicianQty = value
    },

  },
  methods: {
    updateTechnicianQty() {
      if (!this.canUpdateTechnicianQty) return

      this.$emit('update-technicians', Number(this.technicianQty))
    },
    customProfileLabel(profile) {
      if (!profile) return ''

      if (profile.name && profile.code) return `${profile.name} (${profile.code})`
      return profile.name || profile.code || String(profile.custom_profile_id || profile.id || '')
    },
    getItemCustomProfileId(item) {
      if (!item || typeof item !== 'object') return null

      return item.custom_profile_id || item.customProfileId || item.custom_profile?.custom_profile_id || item.customProfile?.custom_profile_id || null
    },
    getItemGpsConfiguration(item) {
      if (!item || typeof item !== 'object') return null

      return item.gps_configuration || item.gpsConfiguration || item.custom_profile?.name || item.customProfile?.name || null
    },
    getPackageGpsConfigurationValue(installPackage) {
      if (this.selectedGpsConfigurations[installPackage.key]) return this.selectedGpsConfigurations[installPackage.key]
      if (!installPackage.customProfileId) return '__default__'

      const currentValue = String(installPackage.customProfileId)
      const selectedOption = this.normalizedGpsConfigurationOptions.find((option) => option.value === currentValue) || this.normalizedGpsConfigurationOptions[0]

      return selectedOption ? selectedOption.value : '__default__'
    },
    updateGpsConfiguration(installPackage, value) {
      if (this.loading || this.loadingCustomProfiles) return

      this.$set(this.selectedGpsConfigurations, installPackage.key, value)

      const selectedOption = this.normalizedGpsConfigurationOptions.find((option) => option.value === value)
      if (!selectedOption) return

      this.$emit('update-gps-configuration', selectedOption.profile, installPackage)
    },
  },
}
</script>

<template>
  <article class="wo-panel wo-install-panel">
    <h2>What to install</h2>

    <p v-if="!installPackages.length" class="wo-install-helper">No install package found</p>

    <div v-for="installPackage in installPackages" :key="installPackage.key" class="wo-package-card">
      <h3>{{ installPackage.title }}</h3>

      <div class="wo-install-grid">
        <div class="wo-install-field wo-install-field--split">
          <strong>{{ installPackage.quantity }}</strong>
          <span>Quantity</span>
        </div>
        <div class="wo-install-field wo-install-field--select">
          <label>GPS CONFIGURATION</label>
          <div>
            <select
              :value="getPackageGpsConfigurationValue(installPackage)"
              class="wo-select-field"
              :disabled="loading || loadingCustomProfiles"
              @change="updateGpsConfiguration(installPackage, $event.target.value)"
            >
              <option
                v-for="option in normalizedGpsConfigurationOptions"
                :key="`${installPackage.key}-${option.value}`"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="wo-install-grid">
      <div class="wo-install-field wo-install-field--split">
        <strong>{{ vehicleCount }}</strong>
        <span>Vehicles</span>
      </div>
      <div class="wo-install-field wo-install-field--select">
        <label>TECHNICIANS NEEDED</label>
        <div>
          <input
            v-model="technicianQty"
            type="number"
            min="0"
            step="1"
            class="wo-select-field"
            :disabled="loading"
            @keyup.enter="updateTechnicianQty"
          />
          <button
            type="button"
            class="wo-action-button wo-action-button--ghost"
            :disabled="!canUpdateTechnicianQty"
            @click="updateTechnicianQty"
          >
            {{ loading ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
    <div class="wo-install-meta">
      <span>Required skills (auto):</span>
      <span class="wo-skill-badge">GPS</span>
      <span class="wo-install-duration">Default install duration: {{ installDuration }}</span>
    </div>
  </article>
</template>
